import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createFamilyMember,
  deleteFamilyMember,
  getFamilyMembers,
  updateFamilyMember,
  uploadFamilyMemberAvatar,
  type FamilyMember,
} from '@/services/familyService';

interface MemberFormState {
  name: string;
  role: string;
  birthDate: string;
}

const EMPTY_FORM: MemberFormState = { name: '', role: '', birthDate: '' };

function displayDateToInput(value: string): string {
  return value ? value.replaceAll('.', '-') : '';
}

export default function AdminFamilyMembersPage() {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [form, setForm] = useState<MemberFormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => a.sortOrder - b.sortOrder),
    [members],
  );

  const loadMembers = async () => {
    try {
      setLoading(true);
      setMembers(await getFamilyMembers());
      setError('');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '가족 구성원을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMembers(); }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setAvatarFile(null);
  };

  const startEdit = (member: FamilyMember) => {
    setEditingId(member.id);
    setForm({
      name: member.name,
      role: member.role,
      birthDate: displayDateToInput(member.birthDate),
    });
    setAvatarFile(null);
    setMessage('');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim() || !form.role.trim()) {
      setError('이름과 가족 관계를 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      setMessage('');
      let member: FamilyMember;
      if (editingId) {
        member = await updateFamilyMember(editingId, form);
      } else {
        member = await createFamilyMember(form);
      }
      if (avatarFile) {
        member = await uploadFamilyMemberAvatar(member.id, avatarFile);
      }
      setMessage(editingId ? '가족 구성원 정보를 수정했습니다.' : '가족 구성원을 추가했습니다.');
      resetForm();
      await loadMembers();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (member: FamilyMember) => {
    if (!window.confirm(`${member.name} 구성원을 삭제할까요?`)) return;
    try {
      setError('');
      await deleteFamilyMember(member.id);
      setMessage('가족 구성원을 삭제했습니다.');
      if (editingId === member.id) resetForm();
      await loadMembers();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : '삭제하지 못했습니다.');
    }
  };

  const moveMember = async (member: FamilyMember, direction: -1 | 1) => {
    const currentIndex = sortedMembers.findIndex((item) => item.id === member.id);
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= sortedMembers.length) return;
    const target = sortedMembers[targetIndex];
    try {
      await Promise.all([
        updateFamilyMember(member.id, { sortOrder: target.sortOrder }),
        updateFamilyMember(target.id, { sortOrder: member.sortOrder }),
      ]);
      await loadMembers();
    } catch (moveError) {
      setError(moveError instanceof Error ? moveError.message : '순서를 변경하지 못했습니다.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="px-4 pt-10 pb-5 border-b border-background-200/70">
        <div className="flex items-center gap-3">
          <Link to="/profile" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-secondary-100" aria-label="뒤로가기">
            <i className="ri-arrow-left-line text-lg"></i>
          </Link>
          <div>
            <span className="text-xs text-accent-500 font-semibold">관리자</span>
            <h1 className="text-xl font-heading font-bold text-foreground-950">가족 구성원 관리</h1>
          </div>
        </div>
      </header>

      <section className="px-4 py-5">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-4 space-y-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-foreground-900">{editingId ? '구성원 수정' : '새 구성원 추가'}</h2>
              <p className="text-xs text-foreground-500 mt-1">6명 제한 없이 계속 추가할 수 있습니다.</p>
            </div>
            {editingId && (
              <button type="button" onClick={resetForm} className="text-xs text-foreground-500 px-3 py-1.5 rounded-full bg-secondary-100">취소</button>
            )}
          </div>

          <label className="block">
            <span className="text-xs font-semibold text-foreground-700">이름</span>
            <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} maxLength={40} className="mt-1.5 w-full rounded-xl border border-background-200 px-3 py-3 text-sm outline-none focus:border-accent-400" placeholder="예: 박봉수" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-foreground-700">가족 관계</span>
            <input value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} maxLength={40} className="mt-1.5 w-full rounded-xl border border-background-200 px-3 py-3 text-sm outline-none focus:border-accent-400" placeholder="예: 아빠, 엄마, 첫째 딸" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-foreground-700">생년월일</span>
            <input type="date" value={form.birthDate} onChange={(event) => setForm({ ...form, birthDate: event.target.value })} className="mt-1.5 w-full rounded-xl border border-background-200 px-3 py-3 text-sm outline-none focus:border-accent-400" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-foreground-700">프로필 사진</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setAvatarFile(event.target.files?.[0] ?? null)} className="mt-1.5 block w-full text-xs text-foreground-600 file:mr-3 file:rounded-full file:border-0 file:bg-accent-100 file:px-3 file:py-2 file:text-accent-700" />
            <p className="text-[11px] text-foreground-400 mt-1">JPG, PNG, WebP · 최대 10MB · 정사각형으로 자동 잘라냅니다.</p>
          </label>

          {error && <div className="px-3 py-2.5 rounded-xl bg-rose-50 text-xs text-rose-600">{error}</div>}
          {message && <div className="px-3 py-2.5 rounded-xl bg-emerald-50 text-xs text-emerald-700">{message}</div>}

          <button type="submit" disabled={saving} className="w-full rounded-xl bg-accent-500 py-3 text-sm font-semibold text-white disabled:opacity-50">
            {saving ? '저장 중...' : editingId ? '수정 내용 저장' : '가족 구성원 추가'}
          </button>
        </form>
      </section>

      <section className="px-4 pb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-foreground-900">등록된 구성원</h2>
          <span className="text-xs text-foreground-400">{members.length}명</span>
        </div>
        {loading ? (
          <div className="py-10 text-center text-sm text-foreground-400">불러오는 중...</div>
        ) : (
          <div className="space-y-3">
            {sortedMembers.map((member, index) => (
              <div key={member.id} className="bg-white rounded-2xl p-3 flex items-center gap-3 shadow-sm">
                <div className="w-14 h-14 rounded-full overflow-hidden bg-accent-100 flex-shrink-0">
                  {member.avatar ? (
                    <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-accent-600 font-bold">{member.name.slice(0, 1)}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground-900 truncate">{member.name}</p>
                  <p className="text-xs text-foreground-500 truncate">{member.role}</p>
                  <p className="text-[11px] text-foreground-400 mt-1">{member.birthDate || '생년월일 미등록'}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex gap-1">
                    <button type="button" disabled={index === 0} onClick={() => moveMember(member, -1)} className="w-8 h-8 rounded-lg bg-secondary-100 disabled:opacity-30" aria-label="앞으로 이동"><i className="ri-arrow-up-s-line"></i></button>
                    <button type="button" disabled={index === sortedMembers.length - 1} onClick={() => moveMember(member, 1)} className="w-8 h-8 rounded-lg bg-secondary-100 disabled:opacity-30" aria-label="뒤로 이동"><i className="ri-arrow-down-s-line"></i></button>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => startEdit(member)} className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700" aria-label="수정"><i className="ri-pencil-line"></i></button>
                    <button type="button" onClick={() => handleDelete(member)} className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600" aria-label="삭제"><i className="ri-delete-bin-line"></i></button>
                  </div>
                </div>
              </div>
            ))}
            {sortedMembers.length === 0 && <div className="py-10 text-center text-sm text-foreground-400">등록된 구성원이 없습니다.</div>}
          </div>
        )}
      </section>
    </div>
  );
}
